import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Users, 
  FileText, 
  Settings, 
  Bell, 
  Menu, 
  X,
  Shield,
  GraduationCap,
  Truck,
  Search,
  UserCog,
  UserPlus,
  Stethoscope,
  FileCode,
  Calendar,
  LogOut,
  Building2,
  Award,
  User,
  Key,
  AlertTriangle,
  Clock,
  Loader2,
  FileSignature,
  CheckCircle,
  Wrench,
  ClipboardCheck,
  Target,
  Package
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import api from '@/services/api';

// Custom People Icon for the logo
const PeopleIcon = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
  >
    {/* Main person */}
    <circle cx="12" cy="6" r="3.5" />
    <path d="M6 17.5c0-3.5 2.7-6 6-6s6 2.5 6 6v1.5H6v-1.5z" />
    {/* Side person left */}
    <circle cx="4" cy="8" r="2" opacity="0.6" />
    <path d="M0 15c0-2.2 1.8-4 4-4s4 1.8 4 4v1H0v-1z" opacity="0.6" />
    {/* Side person right */}
    <circle cx="20" cy="8" r="2" opacity="0.6" />
    <path d="M16 15c0-2.2 1.8-4 4-4s4 1.8 4 4v1h-8v-1z" opacity="0.6" />
  </svg>
);
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useApp } from '@/context/AppContext';

// Avatars prédéfinis - doit correspondre à MonProfil.jsx
const PREDEFINED_AVATARS = [
  { id: 'default-blue', color: 'bg-blue-500', initials: true },
  { id: 'default-green', color: 'bg-green-500', initials: true },
  { id: 'default-purple', color: 'bg-purple-500', initials: true },
  { id: 'default-orange', color: 'bg-orange-500', initials: true },
  { id: 'default-red', color: 'bg-red-500', initials: true },
  { id: 'default-teal', color: 'bg-teal-500', initials: true },
  { id: 'default-pink', color: 'bg-pink-500', initials: true },
  { id: 'default-indigo', color: 'bg-indigo-500', initials: true },
  { id: 'worker-helmet', icon: '👷', label: 'Ouvrier' },
  { id: 'engineer', icon: '🔧', label: 'Technicien' },
  { id: 'safety', icon: '🦺', label: 'HSE' },
  { id: 'manager', icon: '👔', label: 'Manager' },
  { id: 'doctor', icon: '🩺', label: 'Médical' },
  { id: 'forklift', icon: '🚜', label: 'Cariste' },
  { id: 'clipboard', icon: '📋', label: 'Contrôleur' },
  { id: 'factory', icon: '🏭', label: 'Production' },
];

// Helper function to render avatar based on avatar ID or photo URL
const renderUserAvatar = (avatarId, initials, size = 'small', photoUrl = null, userId = null, photoData = null, hasPhoto = false) => {
  const sizeClasses = {
    small: 'w-9 h-9 text-sm',
    medium: 'w-12 h-12 text-base',
  };
  
  // Priority 1: If user has photo_data (base64), use it directly
  if (photoData && photoData.startsWith('data:')) {
    return (
      <div className={`${sizeClasses[size]} rounded-full overflow-hidden bg-gray-200`}>
        <img 
          src={photoData} 
          alt="Photo de profil" 
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = `<div class="w-full h-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">${initials || 'U'}</div>`;
          }}
        />
      </div>
    );
  }
  
  // Priority 2: If user has a photo (photo_url, has_photo flag, or photo exists in DB), fetch via API
  if ((photoUrl || hasPhoto) && userId) {
    const apiBaseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const fullPhotoUrl = `${apiBaseUrl}/api/profile/${userId}/photo`;
    return (
      <div className={`${sizeClasses[size]} rounded-full overflow-hidden bg-gray-200`}>
        <img 
          src={fullPhotoUrl} 
          alt="Photo de profil" 
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback to initials if image fails to load
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = `<div class="w-full h-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">${initials || 'U'}</div>`;
          }}
        />
      </div>
    );
  }
  
  const avatar = PREDEFINED_AVATARS.find(a => a.id === avatarId);
  
  if (avatar) {
    if (avatar.initials) {
      return (
        <div className={`${sizeClasses[size]} ${avatar.color} rounded-full flex items-center justify-center text-white font-semibold`}>
          {initials}
        </div>
      );
    } else if (avatar.icon) {
      return (
        <div className={`${sizeClasses[size]} bg-gray-100 rounded-full flex items-center justify-center`}>
          <span className="text-lg">{avatar.icon}</span>
        </div>
      );
    }
  }
  
  // Default avatar (blue with initials)
  return (
    <div className={`${sizeClasses[size]} bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold`}>
      {initials}
    </div>
  );
};

const navigation = [
  { name: 'Tableau de bord', href: '/', icon: Home },
  
  // Section: Collaborateurs
  { 
    section: 'Collaborateurs',
    items: [
      { name: 'Liste collaborateurs', href: '/collaborateurs', icon: Users },
      { name: 'Visites médicales', href: '/visites-medicales', icon: Stethoscope },
      { name: 'Gestion des compétences', href: '/competences', icon: Target },
    ]
  },
  
  // Section: Habilitations
  { 
    section: 'Habilitations',
    items: [
      { name: 'Autorisations internes', href: '/autorisations', icon: Shield },
      { name: 'CACES et habilitations', href: '/caces', icon: Award },
      { name: 'Formations', href: '/formations', icon: GraduationCap },
      { name: 'Planning formations', href: '/planning-formations', icon: Calendar },
    ]
  },
  
  // Section: Demandes
  { 
    section: 'Demandes',
    items: [
      { name: 'Demandes HSE', href: '/epi/demandes', icon: Package },
      { name: 'Demandes d\'autorisations', href: '/demandes', icon: FileText },
    ]
  },
  
  // Section: Équipements
  { 
    section: 'Équipements',
    items: [
      { name: 'Gestion machines & Compétences', href: '/machines-competences', icon: Wrench },
      { name: 'Matériel', href: '/materiel', icon: Truck },
    ]
  },
];

const settingsNavigation = [
  // Section: Gestion des Utilisateurs
  { 
    section: 'Utilisateurs & Organisation',
    items: [
      { name: 'Gestion des rôles', href: '/roles', icon: UserCog },
      { name: 'Invitations', href: '/invitations', icon: UserPlus },
      { name: 'Secteurs', href: '/secteurs', icon: Building2 },
    ]
  },
  
  // Section: Configuration Formations
  { 
    section: 'Formations & Compétences',
    items: [
      { name: 'Types de formations', href: '/gestion-formations', icon: GraduationCap },
    ]
  },
  
  // Section: Configuration Autorisations
  { 
    section: 'Autorisations & Habilitations',
    items: [
      { name: 'Catégories CACES et habilitations', href: '/categories-caces', icon: Award },
    ]
  },
  
  // Section: Configuration Santé
  { 
    section: 'Santé & Sécurité',
    items: [
      { name: 'Types de visites médicales', href: '/types-visites', icon: Stethoscope },
      { name: 'Types de demandes HSE', href: '/epi/types', icon: Package },
    ]
  },
  
  // Section: Configuration Matériel
  { 
    section: 'Équipements & Matériel',
    items: [
      { name: 'Types de matériel', href: '/types-materiel', icon: Truck },
    ]
  },
  
  // Section: Système
  { 
    section: 'Système',
    items: [
      { name: 'Gestion des modèles', href: '/gestion-modeles', icon: FileCode },
    ]
  },
];

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [settings, setSettings] = useState({
    company_name: 'Jeld-Wen France SAS',
    dpo_email: 'dpo@jeldwen.com'
  });
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Read notifications state (stored in localStorage)
  const [readNotifications, setReadNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem('readNotifications');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout, hasRole, canManageSettings, canManageVisitesMedicales, canManageAutorisations } = useApp();

  // Save read notifications to localStorage
  useEffect(() => {
    localStorage.setItem('readNotifications', JSON.stringify(readNotifications));
  }, [readNotifications]);

  // Mark notification as read
  const markNotificationAsRead = (notifId) => {
    if (!readNotifications.includes(notifId)) {
      setReadNotifications(prev => [...prev, notifId]);
    }
  };

  // Get unread notifications count
  const unreadNotifications = notifications.filter(n => !readNotifications.includes(n.id));
  const unreadCount = unreadNotifications.length;

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults(null);
      setShowSearchResults(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const results = await api.globalSearch(query, 15);
      setSearchResults(results);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // Handle search result click
  const handleResultClick = (type, id) => {
    setShowSearchResults(false);
    setSearchQuery('');
    
    switch (type) {
      case 'collaborateur':
        navigate(`/collaborateurs/${id}`);
        break;
      case 'autorisation':
        navigate(`/demandes/${id}`);
        break;
      case 'materiel':
        navigate(`/materiel/${id}/checklist`);
        break;
      default:
        break;
    }
  };

  // Handle search submit (Enter key)
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.length >= 2) {
      performSearch(searchQuery);
    }
  };

  // Load public settings for footer
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await api.getPublicSettings();
        if (data) {
          setSettings(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        // Use defaults if settings can't be loaded
      }
    };
    loadSettings();
  }, []);

  // Load notifications on mount
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        // Use the new notifications endpoint that returns role-based notifications
        const notifs = await api.getNotifications();
        // Filter unread notifications
        const unreadNotifs = notifs.filter(n => !n.lue).slice(0, 15);
        setNotifications(unreadNotifs);
      } catch (error) {
        console.error('Error loading notifications:', error);
        // Fallback to expirations if notifications endpoint fails
        try {
          const expirations = await api.getUpcomingExpirations();
          const urgentItems = expirations.filter(item => {
            const daysUntil = Math.ceil((new Date(item.date_fin) - new Date()) / (1000 * 60 * 60 * 24));
            return daysUntil <= 30;
          }).slice(0, 10);
          setNotifications(urgentItems);
        } catch (e) {
          console.error('Error loading expirations:', e);
        }
      }
    };
    
    if (currentUser && currentUser.role !== 'Collaborateur') {
      loadNotifications();
      // Refresh every 5 minutes
      const interval = setInterval(loadNotifications, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Define role-based navigation access
  const roleBasedNavigation = {
    '/': ['Admin', 'HSE', 'Directeur', 'RH', 'Manager', 'Collaborateur', 'Maintenance'], // Dashboard for all
    '/collaborateurs': ['Admin', 'HSE', 'Directeur', 'RH', 'Manager'],
    '/visites-medicales': ['Admin', 'HSE', 'Directeur', 'RH'],
    '/competences': ['Admin', 'HSE', 'RH', 'Manager'],  // Gestion des compétences
    '/autorisations': ['Admin', 'HSE', 'Directeur', 'Maintenance', 'Manager'],
    '/caces': ['Admin', 'HSE', 'Directeur', 'RH', 'Manager'],
    '/formations': ['Admin', 'HSE', 'RH', 'Manager'],
    '/planning-formations': ['Admin', 'HSE', 'RH', 'Manager'],
    '/epi/demandes': ['Admin', 'HSE', 'Manager'],
    '/demandes': ['Admin', 'HSE', 'Directeur', 'Manager'],
    '/machines-competences': ['Admin', 'HSE', 'RH', 'Manager'],
    '/materiel': ['Admin', 'HSE', 'Manager', 'Maintenance'],
  };

  const roleBasedSettings = {
    '/roles': ['Admin'],
    '/invitations': ['Admin'],
    '/secteurs': ['Admin', 'HSE'],
    '/categories-caces': ['Admin', 'HSE'],
    '/types-visites': ['Admin', 'RH', 'HSE'],
    '/gestion-formations': ['Admin', 'HSE', 'RH'],
    '/types-materiel': ['Admin', 'HSE'],
    '/gestion-modeles': ['Admin', 'HSE'],
    '/epi/types': ['Admin', 'HSE'],
    '/parametres-systeme': ['Admin'],
  };

  // Filter navigation based on user role - now handling sections
  const filteredNavigation = navigation.map(item => {
    if (item.section && item.items) {
      // C'est une section avec des items
      const filteredItems = item.items.filter(subItem => {
        const allowedRoles = roleBasedNavigation[subItem.href];
        if (!allowedRoles) return true;
        return hasRole(allowedRoles);
      });
      return filteredItems.length > 0 ? { ...item, items: filteredItems } : null;
    } else if (item.href) {
      // C'est un item simple (comme Tableau de bord)
      const allowedRoles = roleBasedNavigation[item.href];
      if (!allowedRoles) return item;
      return hasRole(allowedRoles) ? item : null;
    }
    return null;
  }).filter(Boolean);

  const filteredSettings = settingsNavigation.map(item => {
    if (item.section && item.items) {
      const filteredItems = item.items.filter(subItem => {
        const allowedRoles = roleBasedSettings[subItem.href];
        if (!allowedRoles) return true;
        return hasRole(allowedRoles);
      });
      return filteredItems.length > 0 ? { ...item, items: filteredItems } : null;
    }
    return null;
  }).filter(Boolean);

  // Check if user has access to at least one settings item
  // This determines if the settings gear icon should be shown
  const hasAnySettingsAccess = Object.entries(roleBasedSettings).some(([, allowedRoles]) => 
    hasRole(allowedRoles)
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/20 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-screen w-72 bg-card border-r border-border
          transform transition-transform duration-200 ease-in-out lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <PeopleIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">People Management</h1>
                <p className="text-xs text-muted-foreground">Gestion des collaborateurs</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
            {filteredNavigation.map((item, index) => {
              // Si c'est une section avec des items
              if (item.section && item.items) {
                return (
                  <div key={item.section} className={index > 0 ? 'pt-4' : ''}>
                    <div className="pb-2">
                      <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {item.section}
                      </p>
                    </div>
                    {item.items.map((subItem) => {
                      const isActive = location.pathname === subItem.href;
                      return (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={`
                            flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                            transition-colors duration-200
                            ${isActive 
                              ? 'bg-primary text-primary-foreground shadow-sm' 
                              : 'text-foreground hover:bg-accent'
                            }
                          `}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <subItem.icon className="w-5 h-5" />
                          <span className="flex-1">{subItem.name}</span>
                          {subItem.badge && (
                            <Badge variant="secondary" className="ml-auto">
                              {subItem.badge}
                            </Badge>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                );
              }
              
              // Sinon c'est un item simple (comme Tableau de bord)
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    transition-colors duration-200
                    ${isActive 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-foreground hover:bg-accent'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="flex-1">{item.name}</span>
                  {item.badge && (
                    <Badge variant="secondary" className="ml-auto">
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section with dropdown menu */}
          <div className="p-4 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-accent hover:bg-accent/80 transition-colors cursor-pointer text-left"
                  data-testid="user-profile-menu-trigger"
                >
                  {renderUserAvatar(currentUser?.avatar, currentUser?.prenom?.charAt(0) || 'U', 'small', currentUser?.photo_url, currentUser?.id, currentUser?.photo_data, currentUser?.has_photo)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {currentUser?.prenom} {currentUser?.nom}
                    </p>
                    <p className="text-xs text-muted-foreground">{currentUser?.role || 'Utilisateur'}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top" className="w-56 mb-2">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{currentUser?.prenom} {currentUser?.nom}</p>
                    <p className="text-xs leading-none text-muted-foreground">{currentUser?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/mon-profil" className="w-full cursor-pointer" data-testid="menu-mon-profil">
                    <User className="w-4 h-4 mr-2" />
                    Gérer mon profil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/politique-confidentialite" className="w-full cursor-pointer" data-testid="menu-charte-rgpd">
                    <Shield className="w-4 h-4 mr-2" />
                    Charte RGPD
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="text-destructive cursor-pointer"
                  onClick={handleLogout}
                  data-testid="menu-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-card/95 backdrop-blur-sm border-b border-border">
          <div className="flex items-center gap-4 px-4 py-3">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden shrink-0"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>

            {/* Search bar - Hidden for Collaborateur role - Now larger and centered */}
            {currentUser?.role !== 'Collaborateur' ? (
              <div className="flex-1 max-w-2xl mx-auto" ref={searchRef}>
                <form onSubmit={handleSearchSubmit} className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Rechercher un collaborateur, une autorisation, un matériel..."
                    className="pl-12 pr-12 py-5 bg-background text-base rounded-xl border-2 focus:border-primary/50 transition-colors"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => searchResults && setShowSearchResults(true)}
                    data-testid="global-search-input"
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground animate-spin" />
                  )}
                
                {/* Search Results Dropdown */}
                {showSearchResults && searchResults && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border z-50 max-h-96 overflow-hidden" data-testid="search-results-dropdown">
                    {searchResults.total === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Aucun résultat pour &laquo;{searchQuery}&raquo;</p>
                      </div>
                    ) : (
                      <div className="overflow-y-auto max-h-80">
                        {/* Collaborateurs */}
                        {searchResults.collaborateurs.length > 0 && (
                          <div>
                            <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                              <Users className="h-3 w-3" />
                              Collaborateurs ({searchResults.collaborateurs.length})
                            </div>
                            {searchResults.collaborateurs.map((collab) => (
                              <div
                                key={collab.id}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                                onClick={() => handleResultClick('collaborateur', collab.id)}
                                data-testid={`search-result-collab-${collab.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                                    {collab.prenom?.charAt(0) || 'C'}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {collab.prenom} {collab.nom}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {collab.matricule} {collab.poste && `• ${collab.poste}`} {collab.secteur_nom && `• ${collab.secteur_nom}`}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Autorisations */}
                        {searchResults.autorisations.length > 0 && (
                          <div>
                            <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                              <Shield className="h-3 w-3" />
                              Autorisations ({searchResults.autorisations.length})
                            </div>
                            {searchResults.autorisations.map((auth) => (
                              <div
                                key={auth.id}
                                className="px-3 py-2 hover:bg-green-50 cursor-pointer border-b last:border-b-0"
                                onClick={() => handleResultClick('autorisation', auth.id)}
                                data-testid={`search-result-auth-${auth.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                    <Shield className="h-4 w-4" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {auth.numero}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {auth.collaborateur_nom} • <span className={`font-medium ${auth.statut === 'Délivrée' ? 'text-green-600' : auth.statut === 'Révoquée' ? 'text-red-600' : 'text-orange-600'}`}>{auth.statut}</span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Matériel */}
                        {searchResults.materiel.length > 0 && (
                          <div>
                            <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                              <Truck className="h-3 w-3" />
                              Matériel ({searchResults.materiel.length})
                            </div>
                            {searchResults.materiel.map((mat) => (
                              <div
                                key={mat.id}
                                className="px-3 py-2 hover:bg-amber-50 cursor-pointer border-b last:border-b-0"
                                onClick={() => handleResultClick('materiel', mat.id)}
                                data-testid={`search-result-mat-${mat.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                                    <Truck className="h-4 w-4" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {mat.code} - {mat.designation}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {mat.marque} {mat.modele && mat.modele} • <span className={`font-medium ${mat.statut === 'disponible' ? 'text-green-600' : mat.statut === 'hors_service' ? 'text-red-600' : 'text-orange-600'}`}>{mat.statut || 'disponible'}</span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Results count footer */}
                    {searchResults.total > 0 && (
                      <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-500 text-center">
                        {searchResults.total} résultat{searchResults.total > 1 ? 's' : ''} trouvé{searchResults.total > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                )}
              </form>
            </div>
            ) : (
              /* Spacer for Collaborateur role when search is hidden */
              <div className="flex-1" />
            )}

            {/* Right side icons - Notifications & Settings */}
            <div className="flex items-center gap-1 shrink-0">
              {/* Notifications Dropdown - Hidden for Collaborateur role */}
              {currentUser?.role !== 'Collaborateur' && (
              <div className="relative">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative"
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
                
                {/* Notifications Panel */}
                {showNotifications && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowNotifications(false)}
                    />
                    <div className="absolute right-0 top-12 w-80 md:w-96 bg-white rounded-lg shadow-lg border z-50 max-h-[480px] overflow-hidden">
                      <div className="p-3 border-b bg-gray-50">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Bell className="h-4 w-4 text-blue-500" />
                          Notifications
                          {unreadCount > 0 && (
                            <Badge variant="destructive" className="ml-auto">{unreadCount} non lue{unreadCount > 1 ? 's' : ''}</Badge>
                          )}
                        </h3>
                      </div>
                      <div className="overflow-y-auto max-h-[380px]">
                        {notifications.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>Aucune notification</p>
                          </div>
                        ) : (
                          notifications.map((item, index) => {
                            // Check if notification is read
                            const isRead = readNotifications.includes(item.id);
                            
                            // Determine priority styling (only for unread)
                            const priorite = item.priorite || 'moyenne';
                            const isPrioriteCritique = priorite === 'critique' && !isRead;
                            const isPrioriteHaute = priorite === 'haute' && !isRead;
                            const isPrioriteMoyenne = priorite === 'moyenne' && !isRead;
                            
                            // Get icon based on notification type
                            const getIcon = () => {
                              const iconClass = `h-4 w-4 ${isRead ? 'text-gray-400' : isPrioriteCritique ? 'text-red-600' : isPrioriteHaute ? 'text-orange-600' : 'text-blue-600'}`;
                              switch(item.type || item.icon) {
                                case 'demande_signature':
                                case 'signature':
                                  return <FileSignature className={iconClass} />;
                                case 'demande_validation':
                                case 'validation':
                                  return <CheckCircle className={iconClass} />;
                                case 'vgp_expiration':
                                case 'vgp_expired':
                                case 'vgp':
                                  return <ClipboardCheck className={iconClass} />;
                                case 'vgp_maintenance':
                                case 'vgp_maintenance_expired':
                                case 'maintenance':
                                  return <Wrench className={iconClass} />;
                                case 'autorisation_expiration':
                                case 'autorisation':
                                  return <Shield className={iconClass} />;
                                case 'warning':
                                  return <AlertTriangle className={iconClass} />;
                                case 'CACES':
                                  return <Shield className={iconClass} />;
                                case 'Visite médicale':
                                  return <Stethoscope className={iconClass} />;
                                case 'Formation':
                                  return <GraduationCap className={iconClass} />;
                                default:
                                  return <Bell className={iconClass} />;
                              }
                            };
                            
                            // Background color based on read status and priority
                            const bgColor = isRead 
                              ? 'bg-white opacity-60' 
                              : isPrioriteCritique ? 'bg-red-50 border-l-4 border-l-red-500' 
                              : isPrioriteHaute ? 'bg-orange-50 border-l-4 border-l-orange-500' 
                              : isPrioriteMoyenne ? 'bg-yellow-50 border-l-4 border-l-yellow-400' 
                              : 'bg-blue-50 border-l-4 border-l-blue-400';
                            
                            // Handle notification click - navigate and mark as read
                            const handleNotificationClick = () => {
                              markNotificationAsRead(item.id);
                              setShowNotifications(false);
                              
                              // Determine target URL - use lien if available, otherwise construct from entity info
                              let targetUrl = item.lien;
                              
                              if (!targetUrl) {
                                // Construct URL based on notification type and entity
                                const entityId = item.entity_id || item.entite_id;
                                const entityType = item.entity_type || item.entite_type;
                                const notifType = item.type || '';
                                
                                if (entityId) {
                                  // Determine URL based on entity type or notification type
                                  if (entityType === 'autorisation' || notifType.includes('demande') || notifType.includes('autorisation') || notifType === 'status_change') {
                                    targetUrl = `/demandes/${entityId}`;
                                  } else if (entityType === 'materiel' || entityType === 'materiel_vgp' || entityType === 'materiel_ct' || notifType.includes('vgp') || notifType.includes('materiel')) {
                                    targetUrl = `/materiel/${entityId}/checklist`;
                                  } else if (entityType === 'collaborateur') {
                                    targetUrl = `/collaborateurs/${entityId}`;
                                  } else {
                                    // Default fallback based on notification type keywords
                                    if (notifType.includes('signature') || notifType.includes('validation')) {
                                      targetUrl = `/demandes/${entityId}`;
                                    } else {
                                      targetUrl = '/';
                                    }
                                  }
                                } else {
                                  // No entity ID - use type-based defaults
                                  if (notifType.includes('demande') || notifType.includes('autorisation')) {
                                    targetUrl = '/demandes';
                                  } else if (notifType.includes('vgp') || notifType.includes('materiel')) {
                                    targetUrl = '/materiel';
                                  } else {
                                    targetUrl = '/';
                                  }
                                }
                              }
                              
                              navigate(targetUrl);
                            };
                            
                            return (
                              <div 
                                key={item.id || index}
                                onClick={handleNotificationClick}
                                className={`block p-3 border-b last:border-b-0 hover:bg-gray-100 transition-colors cursor-pointer ${bgColor}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`p-2 rounded-full flex-shrink-0 ${
                                    isRead ? 'bg-gray-100' :
                                    isPrioriteCritique ? 'bg-red-100' : 
                                    isPrioriteHaute ? 'bg-orange-100' : 
                                    isPrioriteMoyenne ? 'bg-yellow-100' : 'bg-blue-100'
                                  }`}>
                                    {getIcon()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className={`text-sm font-semibold truncate ${
                                        isRead ? 'text-gray-500' :
                                        isPrioriteCritique ? 'text-red-800' : 
                                        isPrioriteHaute ? 'text-orange-800' : 'text-gray-900'
                                      }`}>
                                        {item.titre || item.type}
                                      </p>
                                      {isRead && (
                                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Lu</span>
                                      )}
                                    </div>
                                    <p className={`text-xs mt-0.5 line-clamp-2 ${isRead ? 'text-gray-400' : 'text-gray-600'}`}>
                                      {item.message || `${item.collaborateur}: ${item.details}`}
                                    </p>
                                    {item.days_until !== undefined && !isRead && (
                                      <p className={`text-xs mt-1 font-medium flex items-center gap-1 ${
                                        item.days_until < 0 ? 'text-red-600' :
                                        item.days_until <= 7 ? 'text-orange-600' : 'text-yellow-600'
                                      }`}>
                                        <Clock className="h-3 w-3" />
                                        {item.days_until < 0 
                                          ? `Échu depuis ${Math.abs(item.days_until)}j` 
                                          : item.days_until === 0 
                                            ? "Expire aujourd'hui"
                                            : `Dans ${item.days_until}j`
                                        }
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      {notifications.length > 0 && (
                        <div className="p-2 border-t bg-gray-50 flex justify-between items-center">
                          <button 
                            onClick={() => {
                              setReadNotifications(notifications.map(n => n.id));
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Tout marquer comme lu
                          </button>
                          <Link 
                            to="/" 
                            onClick={() => setShowNotifications(false)}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            Tableau de bord
                          </Link>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              )}

              {/* Settings dropdown - Only shown if user has access to at least one settings item */}
              {hasAnySettingsAccess && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="settings-menu-trigger">
                      <Settings className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 max-h-[80vh] overflow-y-auto">
                    <DropdownMenuLabel>Configuration</DropdownMenuLabel>
                    
                    {/* Section: Administration (Admin uniquement) */}
                    {hasRole(['Admin']) && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/roles" className="w-full cursor-pointer">
                            <UserCog className="w-4 h-4 mr-2" />
                            Gestion des rôles
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/invitations" className="w-full cursor-pointer">
                            <UserPlus className="w-4 h-4 mr-2" />
                            Invitations
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/parametres-systeme" className="w-full cursor-pointer">
                            <Settings className="w-4 h-4 mr-2" />
                            Paramètres système
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {/* Section: Organisation (Admin, HSE) */}
                    {hasRole(['Admin', 'HSE']) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Organisation</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <Link to="/secteurs" className="w-full cursor-pointer">
                            <Building2 className="w-4 h-4 mr-2" />
                            Secteurs
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {/* Section: Configuration Habilitations (Admin, HSE) */}
                    {hasRole(['Admin', 'HSE']) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Habilitations</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <Link to="/categories-caces" className="w-full cursor-pointer">
                            <Award className="w-4 h-4 mr-2" />
                            Catégories CACES et habilitations
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {/* Section: Configuration Formations (Admin, HSE, RH) */}
                    {hasRole(['Admin', 'HSE', 'RH']) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Formations</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <Link to="/gestion-formations" className="w-full cursor-pointer">
                            <GraduationCap className="w-4 h-4 mr-2" />
                            Types de formations
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {/* Section: Configuration Santé (Admin, HSE, RH) */}
                    {hasRole(['Admin', 'HSE', 'RH']) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Santé</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <Link to="/types-visites" className="w-full cursor-pointer">
                            <Stethoscope className="w-4 h-4 mr-2" />
                            Types de visites médicales
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {/* Section: Configuration EPI (Admin, HSE) */}
                    {hasRole(['Admin', 'HSE']) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>EPI / HSE</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <Link to="/epi/types" className="w-full cursor-pointer">
                            <Package className="w-4 h-4 mr-2" />
                            Types de demandes HSE
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {/* Section: Configuration Équipements (Admin, HSE) */}
                    {hasRole(['Admin', 'HSE']) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Équipements</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <Link to="/types-materiel" className="w-full cursor-pointer">
                            <Truck className="w-4 h-4 mr-2" />
                            Types de matériel
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/gestion-modeles" className="w-full cursor-pointer">
                            <FileCode className="w-4 h-4 mr-2" />
                            Modèles de documents
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          {children}
        </main>
        
        {/* Footer with RGPD link */}
        <footer className="mt-auto py-4 px-6 border-t bg-gray-50">
          <div className="flex flex-col sm:flex-row items-center justify-between text-sm text-gray-500 gap-2">
            <span>© {new Date().getFullYear()} {settings.company_name}</span>
            <div className="flex items-center gap-4">
              <Link to="/politique-confidentialite" className="hover:text-blue-600 hover:underline flex items-center gap-1">
                <Shield className="h-3 w-3" />
                Politique de confidentialité
              </Link>
              <span>|</span>
              <a href={`mailto:${settings.dpo_email}`} className="hover:text-blue-600 hover:underline">
                Contact DPO
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};
